import React, { useEffect, useRef, useState } from 'react';
import { API_RULESYS_LIST } from './EsConstants';
import { esRead } from './EsFetch';
import { EsSelect } from './EsPrime';
import { useEsStrings } from './EsStrings';

/**
 * The rulesys as the dropdowns list them, in their sort order: { rulesys, catchallId }.
 * catchallId is null until the list arrives, and stays null if it never does. startId is the
 * row esback marks preselect (D&D 5.5), else the catch-all: where a new thing starts.
 * loaded is true once the read has answered or failed.
 */
export function useEsRulesys() {
    // One state, so the list and loaded land in the same render.
    const [ { rulesys, loaded }, setRead ] = useState( { rulesys: [], loaded: false } );

    useEffect( () => {
        let aLive = true;
        esRead( API_RULESYS_LIST )
            .then( ( pList ) => { if ( aLive ) setRead( { rulesys: Array.isArray( pList ) ? pList : [], loaded: true } ); } )
            // No list is a dropdown with nothing in it and a save that names no
            // rulesys: a campaign or character esback files under the catch-all, a pack
            // esback refuses. Nothing to say here.
            .catch( () => { if ( aLive ) setRead( { rulesys: [], loaded: true } ); } );
        return () => { aLive = false; };
    }, [] );

    const catchallId = rulesys.find( ( pRow ) => pRow.catchall )?.id ?? null;
    return { rulesys, catchallId, startId: rulesys.find( ( pRow ) => pRow.preselect )?.id ?? catchallId,
        loaded };
}

/**
 * The Rulesys dropdown, for a pack and for a campaign. value is a rulesys id; a
 * null value is shown as the starting row and handed back through onChange once the
 * list arrives, so what the form sends is what the box shows. The starting row is
 * D&D 5.5 on a form that makes something (fresh), and the catch-all on one that edits,
 * where a null is a saved Homebrew and must not turn into another game.
 *
 * onLoaded is told once the list has answered, in the same commit as the starting row's
 * onChange, so the two land in the caller together. A form that makes something holds
 * its Save until then: pressed earlier, it sends a null, and esback files the new thing
 * under the catch-all, not the row the box was about to show (a pack it refuses).
 */
export default function EsRulesysSelect( { id, name = 'rulesysId', value, onChange, onLoaded, disabled, className,
    fresh = false, 'data-testid': dataTestId } ) {
    const t = useEsStrings();
    const { rulesys, catchallId, startId, loaded } = useEsRulesys();
    const aStart = fresh ? startId : catchallId;

    // Read through refs, so a caller's fresh arrow does not re-run either effect: the
    // campaign page passes one that saves, and every render before the saved value came
    // back would have handed the starting row over again.
    const changed = useRef( onChange );
    changed.current = onChange;
    const read = useRef( onLoaded );
    read.current = onLoaded;

    useEffect( () => {
        if ( value == null && aStart != null ) changed.current?.( { target: { name, value: aStart } } );
    }, [ value, aStart, name ] );

    useEffect( () => {
        if ( loaded ) read.current?.();
    }, [ loaded ] );

    return (
        <EsSelect id={ id } name={ name } value={ value ?? aStart }
                  options={ rulesys } optionLabel="name" optionValue="id"
                  disabled={ disabled } className={ className }
                  emptyMessage={ t( 'common-rulesys-select-empty', 'No rulesystems' ) }
                  aria-label={ t( 'common-rulesys-select', 'Rulesystem' ) }
                  data-testid={ dataTestId }
                  onChange={ onChange } />
    );
}
